import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import AllInclusiveRounded from '@mui/icons-material/AllInclusiveRounded';
import { fetchExpectedContactRate } from '../../services/contactOptimizationService';
import type { ContactRateScenario, ExpectedContactRate } from '../../types/contactOptimization';
import { MetricTile } from './MetricTile';

/**
 * Expected contact rate, above the roster it describes.
 *
 * Per LEAD -- "will we ever reach this person" -- against the per-DIAL
 * connect rate elsewhere in the product. The two differ by roughly five
 * times, the customer tracks the first as Scorecard KPI #1, and it is the one
 * the business plans marketing spend against.
 *
 * Three numbers, and the middle one has to match the roster underneath it.
 * That was got wrong first time: the tile showed "today's roster" at 9.8
 * hours per agent while the grid below it showed the OPTIMIZED roster at
 * 8, so the headline described a different schedule than the thing it sat
 * on top of. It now picks the scenario matching ``shift_work_hours`` from
 * the roster actually being displayed.
 *
 * The backend returns one scenario per supported shift length. The Plan
 * page's roster-view toggle (8/10/12h) changes which roster is on screen,
 * and the middle tile follows it from here -- ``shift_work_hours`` is
 * echoed by the roster response itself rather than read from a constant,
 * so the tile and the grid cannot describe different schedules.
 */

function pct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined) return '–';
  return `${(v * 100).toFixed(digits)}%`;
}

export function useExpectedContactRate() {
  const [data, setData] = React.useState<ExpectedContactRate | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    fetchExpectedContactRate()
      .then((d) => !cancelled && setData(d))
      // Silent: this rides alongside the roster, and a failure here should
      // never take down the schedule the page is built around.
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading };
}

/** Which roster the grid below is showing: today's real schedule, or a
 * solved roster on a shift of this many hours. */
export type RosterViewSelection = { kind: 'current' } | { kind: 'shift'; hours: number };

/** The scenario describing the roster on screen.
 *
 * Never guesses. An earlier version fell back to ``scenarios[0]`` when it
 * could not tell which roster was on screen, and ``scenarios[0]`` is
 * "Today's roster" -- so a payload cached before the roster reported its
 * own shift length rendered a 9.8-hour, 20.1% headline directly above an
 * 8-hour, 16.2% grid. Showing nothing is the correct answer when the
 * match is unknown: a missing tile is visibly missing, a wrong one is not.
 */
function scenarioForView(
  data: ExpectedContactRate,
  view: RosterViewSelection,
): ContactRateScenario | null {
  const scenarios = data.scenarios ?? [];
  if (view.kind === 'current') {
    // The backend emits this one first and labels it "Today's roster"
    // (contact_rate.py). Matched by label rather than by index so a new
    // scenario appearing at the front cannot silently re-point this.
    return scenarios.find((s) => /today/i.test(s.label)) ?? null;
  }
  return (
    scenarios.find(
      (s) => s.hours_per_agent != null && Math.abs(s.hours_per_agent - view.hours) < 0.25,
    ) ?? null
  );
}

export function ExpectedContactRateTiles({
  data,
  loading,
  view,
  updating = false,
}: {
  data: ExpectedContactRate | null;
  loading: boolean;
  view: RosterViewSelection;
  /** A roster view is being rebuilt. Only the middle tile depends on which
   * roster is on screen; the measured history and the no-limit ceiling do
   * not move, so they keep their values rather than flashing to skeletons
   * for something that cannot change. */
  updating?: boolean;
}) {
  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" width={248} height={132} />
        ))}
      </Stack>
    );
  }
  if (!data?.available) return null;

  const roster = scenarioForView(data, view);
  const observed = data.observed_contact_rate;
  // The same qualifier applies to all three: a lead counts as reached only
  // if we got hold of it within this many days of arriving. It bounds the
  // measured tile and both forecasts alike, so it sits on each of them
  // rather than being explained once in a tooltip nobody opens.
  const maturityBadge = `Maturity period · ${data.horizon_days} days`;
  const uncapped = data.uncapped;
  const deltaPts =
    observed != null && roster?.contact_rate != null ? (roster.contact_rate - observed) * 100 : null;

  return (
    <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ width: 248 }}>
        <MetricTile
          icon={HistoryRounded}
          label="Historical contact rate"
          value={pct(observed)}
          badge={maturityBadge}
          tooltip={
            `Out of every 100 leads we bought, how many did we actually get hold of at least once? ` +
            `${
              data.observed_leads
                ? `Measured on the ${data.observed_leads.toLocaleString()} leads that arrived `
                : 'Measured on leads that arrived '
            }` +
            `${
              data.observed_window_days_ago
                ? `${data.observed_window_days_ago.from} to ${data.observed_window_days_ago.to} days ago`
                : 'far enough back'
            }` +
            ` — far enough back that all of them have had their full ${data.horizon_days} days, so none are ` +
            `still in progress. This one really happened. The two tiles beside it are forecasts, and this is ` +
            `what they should be judged against.`
          }
        />
      </Box>

      {updating ? (
        // Stretches to whatever the real tiles beside it measure. A fixed
        // height guessed wrong and left a short grey box sitting between
        // two taller cards.
        <Box sx={{ width: 248, display: 'flex' }}>
          <Skeleton variant="rounded" sx={{ flex: 1, height: 'auto', transform: 'none' }} />
        </Box>
      ) : roster?.contact_rate != null && (
        <Box sx={{ width: 248 }}>
          <MetricTile
            icon={GroupsRounded}
            label="Expected without AI voice agents"
            value={pct(roster.contact_rate)}
            delta={deltaPts != null ? `${deltaPts >= 0 ? '+' : ''}${deltaPts.toFixed(1)}pt` : undefined}
            deltaGood={deltaPts != null ? deltaPts > 0 : null}
            deltaDirection={deltaPts == null ? null : deltaPts >= 0.05 ? 'up' : deltaPts <= -0.05 ? 'down' : null}
            badge={maturityBadge}
            tooltip={
              `If we ran the schedule below, this is the share of new leads we'd expect to reach at least once ` +
              `within ${data.horizon_days} days. Every agent works ${roster.hours_per_agent} hours, which buys ` +
              `about ${roster.dials_per_day.toLocaleString()} calls a day, spent on the ` +
              `~${data.arrivals_per_day.toLocaleString()} leads arriving each day and on the days that follow. ` +
              `The ${data.backlog_leads.toLocaleString()} older leads still in the system are NOT counted — ` +
              `this asks what the schedule does for new business, so the measured rate beside it is not a ` +
              `like-for-like comparison, because that one was earned while agents also worked the backlog. ` +
              `When each call happens matters as much as how many: calling a lead the day it arrives works ` +
              `several times better than calling a week later.`
            }
          />
        </Box>
      )}

      {uncapped?.contact_rate != null && (
        <Box sx={{ width: 248 }}>
          <MetricTile
            icon={AllInclusiveRounded}
            label="Expected with AI voice agents"
            value={pct(uncapped.contact_rate)}
            badge={maturityBadge}
            tooltip={
              `What we could reach if capacity stopped being the limit — every lead called as often as the ` +
              `rules allow, without needing more people on the floor. Two things still hold it down: we may ` +
              `only call in legal calling hours, and we may call the same person at most ` +
              `${data.max_attempts_per_day ?? 4} times a day, across ${data.horizon_days} days. ` +
              `It assumes an AI call is answered as often as a human one — if that turns out lower, so does ` +
              `this number. Anything above it cannot be reached by adding capacity at all; it has to come ` +
              `from making each call land more often.`
            }
          />
        </Box>
      )}
    </Stack>
  );
}
